import { infiniteQueryOptions, queryOptions, useQuery } from '@tanstack/react-query';
import type {
  ResellerCustomer,
  ResellerPortalSummary,
  ResellerSettlement,
  ResellerUsage,
  ApiKeyObject,
  CryptoDeposit,
  PublicCryptoNetwork,
  PublicLegalDoc,
  LegalDoc,
  TopupPromotion,
  TopupTerms,
  AssetGroup,
  AssetObject,
  LedgerEntry,
  Overview,
  Page,
  PortalModel,
  PublicModel,
  PublicSite,
  ReferralCommission,
  ReferralSummary,
  RequestLog,
  StorageUsage,
  TaskDetail,
  TaskStatus,
  TaskWithKey,
} from '@aitoken/contracts';
import { TERMINAL_TASK_STATUSES } from '@aitoken/contracts';
import { api, fetchMe, ME_QUERY_KEY } from './api';

export const PAGE_SIZE = 20;

/** 站点信息缺失时的兜底（后端未就绪时仍可渲染公开页面）。 */
export const FALLBACK_SITE: PublicSite = {
  site_id: 'cn',
  name: 'aitoken.tools',
  currency: 'CNY',
  timezone: 'Asia/Shanghai',
  locale: 'zh-CN',
  api_base_url: 'https://api.example.com',
  other_site_url: null,
  contact: { email: null, wechat: null, note: null },
  announcement: null,
  human_check: null,
  turnstile_site_key: null,
};

export const siteQuery = queryOptions({
  queryKey: ['public', 'site'],
  queryFn: () => api.get<PublicSite>('/portal/public/site'),
  staleTime: 10 * 60_000,
});

export function useSite(): PublicSite {
  const { data } = useQuery(siteQuery);
  return data ?? FALLBACK_SITE;
}

export const meQuery = queryOptions({
  queryKey: ME_QUERY_KEY,
  queryFn: fetchMe,
  staleTime: 60_000,
  retry: false,
});

export const publicModelsQuery = queryOptions({
  queryKey: ['public', 'models'],
  queryFn: () => api.get<{ data: PublicModel[] }>('/portal/public/models').then((r) => r.data),
  staleTime: 5 * 60_000,
});

/** 首页成功率（后台“首页成功率”里开启展示的模型） */
export interface PublicModelStats {
  window_days: number;
  models: { model: string; display_name: string; success_rate: number; source: 'measured' | 'configured' }[];
}

export const publicModelStatsQuery = queryOptions({
  queryKey: ['public', 'model-stats'],
  queryFn: () => api.get<PublicModelStats>('/portal/public/model-stats'),
  staleTime: 5 * 60_000,
});

export const portalModelsQuery = queryOptions({
  queryKey: ['portal', 'models'],
  queryFn: () => api.get<{ data: PortalModel[] }>('/portal/models').then((r) => r.data),
  staleTime: 60_000,
});

export const overviewQuery = queryOptions({
  queryKey: ['portal', 'overview'],
  queryFn: () => api.get<Overview>('/portal/overview'),
});

export const keysQuery = queryOptions({
  queryKey: ['portal', 'keys'],
  queryFn: () => api.get<Page<ApiKeyObject>>('/portal/keys', { limit: 100 }),
});

export const storageQuery = queryOptions({
  queryKey: ['portal', 'storage'],
  queryFn: () => api.get<StorageUsage>('/portal/storage'),
});

export const assetGroupsQuery = queryOptions({
  queryKey: ['portal', 'asset-groups'],
  queryFn: () => api.get<{ data: AssetGroup[] }>('/portal/asset-groups').then((r) => r.data),
});

export interface AssetFilters {
  group_id?: string;
  kind?: string;
  status?: string;
  q?: string;
}

const ASSET_PENDING = new Set(['uploading', 'processing', 'deleting']);

export function assetsQuery(filters: AssetFilters) {
  return infiniteQueryOptions({
    queryKey: ['portal', 'assets', filters],
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<AssetObject>>(
        '/portal/assets',
        { ...filters, limit: 24, after: pageParam ?? undefined },
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.has_more ? last.next_cursor : null),
    // 有素材仍在处理时每 5 秒刷新，直到全部落定
    refetchInterval: (query) =>
      query.state.data?.pages.some((p) => p.data.some((a) => ASSET_PENDING.has(a.status))) ? 5000 : false,
  });
}

export function assetQuery(id: string) {
  return queryOptions({
    queryKey: ['portal', 'asset', id],
    queryFn: () => api.get<AssetObject>(`/portal/assets/${id}`),
    refetchInterval: (query) =>
      query.state.data && ASSET_PENDING.has(query.state.data.status) ? 5000 : false,
  });
}

export function assetPreviewQuery(id: string) {
  return queryOptions({
    queryKey: ['portal', 'asset-preview', id],
    queryFn: () => api.get<{ url: string; expires_at: string }>(`/portal/assets/${id}/preview`),
    // 预览地址是短期签名：在过期前刷新
    staleTime: 4 * 60_000,
    gcTime: 5 * 60_000,
  });
}

export function isTerminal(status: TaskStatus): boolean {
  return TERMINAL_TASK_STATUSES.includes(status);
}

export interface TaskFilters {
  status?: string;
  model?: string;
  key_id?: string;
  from?: string;
  to?: string;
}

export function tasksQuery(filters: TaskFilters, limit = PAGE_SIZE) {
  return infiniteQueryOptions({
    queryKey: ['portal', 'tasks', filters, limit],
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<TaskWithKey>>(
        '/portal/tasks',
        { ...filters, limit, after: pageParam ?? undefined },
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.has_more ? last.next_cursor : null),
    // 非终态行每 5 秒轮询，全部终态即停
    refetchInterval: (query) =>
      query.state.data?.pages.some((p) => p.data.some((t) => !isTerminal(t.status))) ? 5000 : false,
  });
}

export function taskQuery(id: string) {
  return queryOptions({
    queryKey: ['portal', 'task', id],
    queryFn: () => api.get<TaskDetail>(`/portal/tasks/${id}`),
    refetchInterval: (query) => (query.state.data && !isTerminal(query.state.data.status) ? 5000 : false),
  });
}

export interface RequestLogFilters {
  status?: string;
  from?: string;
  to?: string;
}

export function requestLogsQuery(filters: RequestLogFilters) {
  return infiniteQueryOptions({
    queryKey: ['portal', 'request-logs', filters],
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<RequestLog>>(
        '/portal/request-logs',
        { ...filters, limit: PAGE_SIZE, after: pageParam ?? undefined },
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.has_more ? last.next_cursor : null),
  });
}

export function ledgerQuery(type?: string) {
  return infiniteQueryOptions({
    queryKey: ['portal', 'ledger', type ?? 'all'],
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<LedgerEntry>>(
        '/portal/ledger',
        { type, limit: PAGE_SIZE, after: pageParam ?? undefined },
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.has_more ? last.next_cursor : null),
  });
}

export const referralSummaryQuery = queryOptions({
  queryKey: ['portal', 'referrals'],
  queryFn: () => api.get<ReferralSummary>('/portal/referrals'),
});

export function referralCommissionsQuery() {
  return infiniteQueryOptions({
    queryKey: ['portal', 'referral-commissions'],
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<ReferralCommission>>(
        '/portal/referrals/commissions',
        { limit: PAGE_SIZE, after: pageParam ?? undefined },
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.has_more ? last.next_cursor : null),
  });
}

/* ---------------- 渠道后台（docs/12） ---------------- */

export const resellerSummaryQuery = queryOptions({
  queryKey: ['portal', 'reseller'],
  queryFn: () => api.get<ResellerPortalSummary>('/portal/reseller'),
});

export function resellerCustomersQuery(q?: string) {
  return infiniteQueryOptions({
    queryKey: ['portal', 'reseller-customers', q ?? ''],
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<ResellerCustomer>>(
        '/portal/reseller/customers',
        { q, limit: PAGE_SIZE, after: pageParam ?? undefined },
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.has_more ? last.next_cursor : null),
  });
}

export function resellerUsageQuery(filters: { month?: string; customer_id?: string }) {
  return infiniteQueryOptions({
    queryKey: ['portal', 'reseller-usage', filters],
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<ResellerUsage>>(
        '/portal/reseller/usage',
        { ...filters, limit: PAGE_SIZE, after: pageParam ?? undefined },
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.has_more ? last.next_cursor : null),
  });
}

export function resellerSettlementsQuery() {
  return infiniteQueryOptions({
    queryKey: ['portal', 'reseller-settlements'],
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<ResellerSettlement>>(
        '/portal/reseller/settlements',
        { limit: PAGE_SIZE, after: pageParam ?? undefined },
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.has_more ? last.next_cursor : null),
  });
}

/* ---------------- 在线充值（docs/14，仅海外站） ---------------- */

export const cryptoNetworksQuery = queryOptions({
  queryKey: ['portal', 'crypto', 'networks'],
  queryFn: () => api.get<{ data: PublicCryptoNetwork[] }>('/portal/billing/crypto/networks').then((r) => r.data),
});

export const topupTermsQuery = queryOptions({
  queryKey: ['portal', 'crypto', 'terms'],
  queryFn: () => api.get<TopupTerms>('/portal/billing/crypto/terms'),
});

export const publicTopupTermsQuery = queryOptions({
  queryKey: ['public', 'topup-terms'],
  queryFn: () => api.get<Omit<TopupTerms, 'accepted'>>('/portal/public/topup-terms'),
});

export const publicLegalQuery = (doc: LegalDoc) =>
  queryOptions({
    queryKey: ['public', 'legal', doc],
    queryFn: () => api.get<PublicLegalDoc>(`/portal/public/legal/${doc}`),
  });

export const topupPromotionsQuery = queryOptions({
  queryKey: ['portal', 'crypto', 'promotions'],
  queryFn: () => api.get<{ data: TopupPromotion[] }>('/portal/billing/crypto/promotions').then((r) => r.data),
});

/** 还在走流程的充值（发现、确认中）：列表每 15 秒刷新一次 */
const OPEN_DEPOSIT = new Set(['detected', 'confirming', 'confirmed']);

export function cryptoDepositsQuery() {
  return infiniteQueryOptions({
    queryKey: ['portal', 'crypto', 'deposits'],
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<CryptoDeposit>>(
        '/portal/billing/crypto/deposits',
        { limit: 10, after: pageParam ?? undefined },
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.has_more ? last.next_cursor : null),
    refetchInterval: (q) =>
      q.state.data?.pages.some((p) => p.data.some((d) => OPEN_DEPOSIT.has(d.status))) ? 15_000 : false,
  });
}
